import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { t_popUp } from "@i18n/usualTexts/t_popUp";

interface Props {
  errorData: string;
  setErrorData: React.Dispatch<React.SetStateAction<string | null>>;
}

const ErrorIcon: React.FC = () => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-12 h-12 shrink-0"
    aria-hidden="true"
  >
    <circle cx="24" cy="24" r="22" fill="var(--surface-secondary)" stroke="var(--accent-primary)" strokeWidth="2" />
    <rect x="22" y="13" width="4" height="16" rx="2" fill="var(--accent-primary)" />
    <circle cx="24" cy="34" r="2.5" fill="var(--accent-primary)" />
  </svg>
);

const isNetworkError = (data: string): boolean => data === "Network Error";

const ServerErrorPopUp: React.FC<Props> = ({ errorData, setErrorData }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (errorData === "Unauthorized") {
      setErrorData(null);
    }
  }, [errorData, setErrorData]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setErrorData(null);
  }, [setErrorData]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeModal]);

  if (errorData === "Unauthorized") return <></>;
  if (!showModal) return null;

  const networkError = isNetworkError(errorData);
  const title = networkError ? t(t_popUp.titleNetworkError) : t(t_popUp.titleError);
  const message = networkError ? t(t_popUp.bodyTextNetworkError) : t(t_popUp.bodyTextError);
  const isErrorCodeLike = /^[A-Z]/.test(errorData) && !errorData.includes(" ");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="server-error-title"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeModal} />

      <div className="relative bg-(--surface-primary) rounded-2xl shadow-2xl max-w-sm w-full animate-in fade-in zoom-in duration-200 overflow-hidden">
        <div className="px-6 pt-6 pb-0 flex flex-col items-center text-center gap-4">
          <ErrorIcon />

          <div className="space-y-2">
            <h2
              id="server-error-title"
              className="text-lg font-semibold text-(--text-primary)"
            >
              {title}
            </h2>
            <p className="text-sm text-(--text-secondary) leading-relaxed">
              {message}
            </p>
          </div>

          {!isErrorCodeLike && !networkError && (
            <p className="text-sm text-(--text-secondary) leading-relaxed">
              {errorData}
            </p>
          )}

          {isErrorCodeLike && (
            <div className="w-full">
              <button
                type="button"
                className="text-xs text-(--text-tertiary,var(--text-secondary)) hover:text-(--text-primary) transition-colors duration-150 cursor-pointer bg-transparent border-0 p-0 inline-flex items-center gap-1"
                onClick={() => setShowDetails(!showDetails)}
                aria-expanded={showDetails}
                aria-controls="error-details-content"
              >
                <span className={`transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}>
                  &#9662;
                </span>
                {t(t_popUp.detailsToggle)}
              </button>

              <div
                id="error-details-content"
                className={`overflow-hidden transition-all duration-200 ease-in-out ${showDetails ? 'max-h-24 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}
              >
                <div className="bg-(--surface-secondary) rounded-lg px-3 py-2">
                  <code className="text-xs text-(--text-secondary) break-all select-all">
                    {errorData}
                  </code>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 pt-4 pb-6">
          <button
            type="button"
            onClick={closeModal}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-150 cursor-pointer border-0 bg-(--button-primary-bg,var(--seed-base)) text-(--button-primary-text,#ffffff) hover:bg-(--button-primary-hover-bg,var(--seed-base))"
            aria-label={t("help.closeAriaLabel")}
          >
            {t(t_popUp.dismissButton)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServerErrorPopUp;
